// HistorialConsultas.js - Rediseño Moderno (moustomp)
import React from 'react';
import {
  Typography, IconButton, Tooltip, CircularProgress,
  Box, Chip, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Info as InfoIcon,
  EventNote as EventNoteIcon,
  HistoryOutlined as HistoryIcon,
  FiberNew as FiberNewIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';

const THEME = {
  primary: '#1a6b4a',
  primaryLight: '#e8f5ee',
  primaryMid: '#2e8b62',
  accent: '#f0a500',
  accentLight: '#fff8e6',
  surface: '#ffffff',
  surfaceAlt: '#f7f9f8',
  border: '#e0ebe6',
  textPrimary: '#0f2d1e',
  textSecondary: '#5a7a6e',
  textMuted: '#93b0a5',
};

const CitaCard = ({ cita, selected, onClick, onInfoClick, showInfo, isMobile }) => {
  const fecha = cita.fecha_cita
    ? dayjs(cita.fecha_cita).format('DD MMM YYYY')
    : 'Sin fecha';
  const tieneConsulta = !!cita.consulta;
  const isNew = tieneConsulta && dayjs().diff(dayjs(cita.consulta?.createdAt), 'day') < 2;

  const card = (
    <Box
      onClick={onClick}
      sx={{
        p: '11px 13px',
        borderRadius: '10px',
        cursor: 'pointer',
        border: `1.5px solid ${selected ? THEME.primary : THEME.border}`,
        bgcolor: selected ? THEME.primaryLight : THEME.surface,
        transition: 'all 0.16s ease',
        position: 'relative',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5,
        '&:hover': {
          borderColor: THEME.primaryMid,
          bgcolor: selected ? THEME.primaryLight : '#f0f7f4',
          transform: 'translateX(2px)',
        },
      }}
    >
      {/* Indicador lateral seleccionado */}
      {selected && (
        <Box sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 3, height: '60%', bgcolor: THEME.primary, borderRadius: '0 2px 2px 0' }} />
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: selected ? THEME.primary : THEME.textPrimary }}>
            {fecha}
          </Typography>
          {isNew && (
            <Chip
              label="Nueva"
              size="small"
              sx={{ height: 16, fontSize: '9px', fontWeight: 700, bgcolor: THEME.accent, color: '#fff', px: 0.2 }}
            />
          )}
        </Box>
        <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.35,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {cita.razon_consulta || 'Sin motivo registrado'}
        </Typography>
        {!tieneConsulta && (
          <Typography sx={{ fontSize: '10px', color: THEME.textMuted, mt: 0.3, fontStyle: 'italic' }}>
            Sin consulta registrada
          </Typography>
        )}
      </Box>

      {/* Botón Info (solo si tiene consulta) */}
      {tieneConsulta && showInfo && (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onInfoClick(e, cita); }}
          sx={{
            p: 0.3, color: THEME.textMuted, flexShrink: 0,
            '&:hover': { color: THEME.primary, bgcolor: THEME.primaryLight },
          }}
        >
          <InfoIcon sx={{ fontSize: 15 }} />
        </IconButton>
      )}
    </Box>
  );

  // Tooltip en desktop
  if (!isMobile && tieneConsulta) {
    return (
      <Tooltip
        key={cita.id_cita}
        placement="right"
        arrow
        enterDelay={400}
        title={
          <Box sx={{ p: 0.5, maxWidth: 220 }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#fff', mb: 0.5 }}>Resumen</Typography>
            {[
              { label: 'Diagnóstico', val: cita.consulta?.diagnostico_patologia },
              { label: 'Tratamiento', val: cita.consulta?.tratamiento_clinica },
            ].map(({ label, val }) => (
              <Box key={label} sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>
                <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.9)' }}>
                  {val ? (val.length > 60 ? val.slice(0, 60) + '...' : val) : 'Sin datos'}
                </Typography>
              </Box>
            ))}
          </Box>
        }
        componentsProps={{ tooltip: { sx: { bgcolor: THEME.textPrimary, borderRadius: '10px', p: 1 } }, arrow: { sx: { color: THEME.textPrimary } } }}
      >
        {card}
      </Tooltip>
    );
  }

  return <React.Fragment key={cita.id_cita}>{card}</React.Fragment>;
};

const HistorialConsultas = ({
  citas, selectedCita, onSelectCita,
  onPopoverOpen, onQuickView, onRefresh, loading,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleInfoClick = (e, cita) => {
    if (isMobile && onQuickView) onQuickView(cita);
    else if (!isMobile && onPopoverOpen) onPopoverOpen(e, cita);
  };

  const showInfo = !!(onQuickView || onPopoverOpen);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon sx={{ fontSize: 16, color: THEME.textSecondary }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: THEME.textPrimary }}>
            Historial
          </Typography>
          {citas.length > 0 && (
            <Chip label={citas.length} size="small"
              sx={{ height: 18, fontSize: '10px', fontWeight: 700, bgcolor: THEME.primaryLight, color: THEME.primary, minWidth: 24 }} />
          )}
        </Box>
        <Tooltip title="Actualizar">
          <IconButton size="small" onClick={onRefresh} disabled={loading}
            sx={{ color: THEME.textMuted, '&:hover': { color: THEME.primary } }}>
            <RefreshIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Lista */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 2, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress size={28} sx={{ color: THEME.primary }} />
          </Box>
        ) : citas.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 6, px: 2 }}>
            <EventNoteIcon sx={{ fontSize: 36, color: THEME.border, mb: 1 }} />
            <Typography sx={{ fontSize: '13px', color: THEME.textMuted }}>
              Sin consultas registradas
            </Typography>
          </Box>
        ) : (
          citas.map((cita) => (
            <CitaCard
              key={cita.id_cita}
              cita={cita}
              selected={selectedCita?.id_cita === cita.id_cita}
              onClick={() => onSelectCita(cita)}
              onInfoClick={handleInfoClick}
              showInfo={showInfo}
              isMobile={isMobile}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default HistorialConsultas;